import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { loginWithCredentials, loginWithGoogle } from "../../utils/keycloak";
import { useNavigate, useLocation, Link, type Location } from "react-router-dom";
import "./Login.scss";
import BackToHomeLink from "./BackToHomeLink";
import { useAuth } from "../../contexts/AuthContext";
import { getBackendUrl } from "../../utils/utils";

const safeGetRedirect = (): string => {
  try {
    return sessionStorage.getItem('postLoginRedirect') || '/';
  } catch {
    return '/';
  }
};

const safeClearRedirect = (): void => {
  try {
    sessionStorage.removeItem('postLoginRedirect');
  } catch { /* storage blocked */ }
};

const Login: React.FC = () => {
  const [error, setError] = useState<string>("");
  const [loginLoading, setLoginLoading] = useState<boolean>(false);
  const [googleLoading, setGoogleLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [setupIncomplete, setSetupIncomplete] = useState<boolean>(false);
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [resendResult, setResendResult] = useState<{ success: boolean; message: string } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, refreshAuth } = useAuth();
  // Show marketplace "Set up your account" footer link when coming from any marketplace flow.
  const isFromMarketplace = new URLSearchParams(location.search).get('marketplace') === '1';
  // Show "Account created" green banner ONLY after a successful /marketplace/register.
  // Using a separate flag prevents the error-page "Go to Login" (which also sets ?marketplace=1)
  // from falsely showing the success banner.
  const justRegistered = new URLSearchParams(location.search).get('registered') === '1';

  // Handle marketplace org_id and SSO error from URL parameters
  React.useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const marketplaceOrgId = urlParams.get('marketplace_org_id');
    if (marketplaceOrgId) {
      sessionStorage.setItem('marketplace_org_id', marketplaceOrgId);
    }
    const ssoError = urlParams.get('error');
    if (ssoError === 'sso_auth_failed' || ssoError === 'sso_component_error') {
      setError("Google sign-in failed. If you have an existing account with this email, please sign in with your email and password instead, or contact support to link your Google account.");
    }
  }, [location.search]);

  React.useEffect(() => {
    if (currentUser) {
      const fromLoc = (location.state as { from?: Location })?.from;
      const redirect = fromLoc ? `${fromLoc.pathname}${fromLoc.search ?? ''}` : safeGetRedirect();
      safeClearRedirect();
      navigate(redirect, { replace: true });
    }
  }, [currentUser, navigate, location.state]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setEmailError("");
    setPasswordError("");

    let hasError = false;
    if (!email) {
      setEmailError("Email is required");
      hasError = true;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError("Please enter a valid email address");
      hasError = true;
    }

    if (!password) {
      setPasswordError("Password is required");
      hasError = true;
    }

    if (hasError) return;

    setLoginLoading(true);

    setSetupIncomplete(false);
    setResendResult(null);

    try {
      const success = await loginWithCredentials(email, password);
      if (success) {
        try {
          await refreshAuth();
        } catch (refreshError) {
          console.error('[Login] Auth state refresh failed after successful login:', refreshError);
          const fromLoc2 = (location.state as { from?: Location })?.from;
          const redirect = fromLoc2 ? `${fromLoc2.pathname}${fromLoc2.search ?? ''}` : safeGetRedirect();
          safeClearRedirect();
          navigate(redirect, { replace: true });
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      if (message.toLowerCase().includes('not fully set up')) {
        setSetupIncomplete(true);
        setError('');
      } else {
        setError(message || "Failed to login. Please check your credentials.");
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleResendSetupEmail = async () => {
    if (!email) return;
    setResendLoading(true);
    setResendResult(null);
    try {
      // This endpoint is public — the user is not authenticated yet (they're on
      // the login page). Use a plain fetch without a Bearer token instead of
      // api.post, which would call getIdToken() and fail for unauthenticated users.
      const response = await fetch(`${getBackendUrl()}/auth/resend-setup-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const result = await response.json() as { success: boolean; message: string };
      setResendResult(result);
    } catch {
      setResendResult({ success: false, message: 'Failed to send email. Please try again.' });
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setGoogleLoading(true);

    try {
      const redirectUri = `${window.location.origin}${location.pathname}${location.search}`;
      await loginWithGoogle(redirectUri);
    } catch (err: any) {
      setError("Failed to redirect to Google login");
      setGoogleLoading(false);
    }
  };

  return (
    <div className="login-page">
      <BackToHomeLink />
      <div className="login-card">
        <div className="login-card-header">
          <img src="/assets/branding/rc-logo-dark.png" alt="Reality Coach" className="login-logo" />
          <h1 className="login-title">Welcome back</h1>
          <p className="login-subtitle">Sign in to your account</p>
        </div>

        {/* Marketplace-only hint — shown only after a successful account creation */}
        {justRegistered && (
          <div style={{
            background: '#f0fdf4',
            border: '1px solid #22c55e',
            borderRadius: '8px',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            fontSize: '0.85rem',
            color: '#15803d',
            lineHeight: 1.5,
          }}>
            <strong>✓ Account created.</strong> Sign in with the email and password you just set up.
          </div>
        )}

        {/* Account setup incomplete banner */}
        {setupIncomplete && (
          <div style={{
            background: '#fff7ed',
            border: '1px solid #fb923c',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
          }}>
            <p style={{ margin: '0 0 0.5rem', fontWeight: 600, color: '#9a3412', fontSize: '0.9rem' }}>
              Account setup incomplete
            </p>
            <p style={{ margin: '0 0 0.75rem', color: '#7c2d12', fontSize: '0.85rem', lineHeight: 1.5 }}>
              Your account setup is not yet complete. If you haven't set a password yet,
              check your inbox for an invitation email with a <strong>"Set your password"</strong> link.
              It may be in your spam folder. If you already set your password and are still
              seeing this, click <strong>Resend setup email</strong> below and use the new link.
            </p>
            <p style={{ margin: '0 0 0.75rem', color: '#7c2d12', fontSize: '0.85rem', lineHeight: 1.5 }}>
              Contact the person who invited you if you continue to have trouble.
            </p>
            {resendResult ? (
              <p style={{
                margin: 0,
                fontWeight: 600,
                fontSize: '0.85rem',
                color: resendResult.success ? '#15803d' : '#9a3412',
              }}>
                {resendResult.message}
              </p>
            ) : (
              <button
                type="button"
                onClick={handleResendSetupEmail}
                disabled={resendLoading || !email}
                style={{
                  background: '#ea580c',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.45rem 1rem',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: resendLoading || !email ? 'not-allowed' : 'pointer',
                  opacity: resendLoading || !email ? 0.6 : 1,
                }}
              >
                {resendLoading ? 'Sending…' : 'Resend setup email'}
              </button>
            )}
          </div>
        )}

        {error && <div className="login-error" role="alert">{error}</div>}

        <form className="login-form" onSubmit={handleLogin} aria-labelledby="login-heading">
          <div className="login-field">
            <label htmlFor="email" className="login-label">Email</label>
            <input
              id="email"
              type="email"
              className={`login-input ${emailError ? "login-input--error" : ""}`}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loginLoading}
              aria-invalid={!!emailError}
              aria-describedby={emailError ? "email-error" : undefined}
            />
            {emailError && <div id="email-error" className="login-field-error">{emailError}</div>}
          </div>

          <div className="login-field">
            <div className="login-label-row">
              <label htmlFor="password" className="login-label">Password</label>
              <a href="/forgot-password" className="login-forgot-link">Forgot password?</a>
            </div>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className={`login-input ${passwordError ? "login-input--error" : ""}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loginLoading}
                aria-invalid={!!passwordError}
                aria-describedby={passwordError ? "password-error" : undefined}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {passwordError && <div id="password-error" className="login-field-error">{passwordError}</div>}
          </div>

          <label className="login-checkbox">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={loginLoading}
            />
            <span>Remember me</span>
          </label>

          <button type="submit" className="login-btn login-btn--primary" disabled={loginLoading}>
            {loginLoading ? <span className="login-spinner" /> : 'Sign in'}
          </button>

          <p style={{ textAlign: "center", fontSize: "0.8125rem", color: "#64748b", margin: 0 }}>
            Don't have an account?{" "}
            <Link to="/signup" className="login-forgot-link">Sign up free</Link>
          </p>

          <div className="login-divider"><span>or</span></div>

          <button
            type="button"
            className="login-btn login-btn--google"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
          >
            {googleLoading ? (
              <span className="login-spinner" />
            ) : (
              <>
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>

          {isFromMarketplace && (
            <div style={{
              marginTop: '1rem',
              paddingTop: '1rem',
              borderTop: '1px solid #e5e7eb',
              textAlign: 'center',
              fontSize: '0.875rem',
              color: '#6b7280',
            }}>
              Purchased via Google Cloud Marketplace?{' '}
              <a
                href="/marketplace-signup"
                style={{ color: '#005581', fontWeight: 600, textDecoration: 'none' }}
              >
                Set up your account →
              </a>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
